import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  denyAdminReadingOrderRequest,
  getAdminReadingOrderRequests,
} from "../../../services/adminApi";

const Requests = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const cancelRef = useRef();
  const [requestToDeny, setRequestToDeny] = useState(null);
  const [denialReason, setDenialReason] = useState("");
  const requestsQuery = useQuery(
    ["adminReadingOrderRequests"],
    getAdminReadingOrderRequests,
    { refetchOnWindowFocus: false }
  );

  const denyMutation = useMutation(
    ({ requestId, reason }) => denyAdminReadingOrderRequest(requestId, reason),
    {
      onSuccess: () => {
        setRequestToDeny(null);
        setDenialReason("");
        queryClient.invalidateQueries(["adminReadingOrderRequests"]);
        queryClient.invalidateQueries(["requests"]);
        toast({
          title: "Request denied.",
          description: "The request was removed from the open queue.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error) => {
        toast({
          title: "Unable to deny request.",
          description:
            error.response?.data?.message ||
            "Please try again or refresh the queue.",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      },
    }
  );

  const closeDenyDialog = () => {
    if (!denyMutation.isLoading) {
      setRequestToDeny(null);
      setDenialReason("");
    }
  };

  const submitDenial = (event) => {
    event.preventDefault();
    const reason = denialReason.trim();

    if (!requestToDeny || !reason) {
      return;
    }

    denyMutation.mutate({ requestId: requestToDeny.id, reason });
  };

  if (requestsQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py="12">
        <Spinner />
      </Box>
    );
  }

  if (requestsQuery.isError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>
          Requests could not be loaded. Please refresh and try again.
        </AlertDescription>
      </Alert>
    );
  }

  const requests = requestsQuery.data?.data || [];

  return (
    <>
      {requests.length === 0 ? (
        <Alert status="success">
          <AlertIcon />
          <AlertDescription>
            No reading order requests are waiting for review.
          </AlertDescription>
        </Alert>
      ) : (
        <TableContainer>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Submitted</Th>
                <Th>Request</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {requests.map((request) => {
                const isDenying =
                  denyMutation.isLoading &&
                  denyMutation.variables?.requestId === request.id;

                return (
                  <Tr key={request.id}>
                    <Td whiteSpace="nowrap">
                      <Text fontSize="sm">{request.submittedAt}</Text>
                    </Td>
                    <Td minW="360px">
                      <Text>{request.description}</Text>
                      <Text color="gray.500" fontSize="xs">
                        Request #{request.id}
                      </Text>
                    </Td>
                    <Td>
                      <Button
                        colorScheme="red"
                        isDisabled={denyMutation.isLoading}
                        isLoading={isDenying}
                        loadingText="Denying"
                        onClick={() => {
                          setRequestToDeny(request);
                          setDenialReason("");
                        }}
                        size="sm"
                      >
                        Deny
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <AlertDialog
        isOpen={!!requestToDeny}
        leastDestructiveRef={cancelRef}
        onClose={closeDenyDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent as="form" onSubmit={submitDenial}>
            <AlertDialogHeader>Deny reading order request?</AlertDialogHeader>
            <AlertDialogBody>
              <Text mb="4">
                This closes the request and shows the reason to the requester.
              </Text>
              <FormControl isRequired>
                <FormLabel htmlFor="denial-reason">Reason for denial</FormLabel>
                <Textarea
                  id="denial-reason"
                  maxLength={255}
                  onChange={(event) => setDenialReason(event.target.value)}
                  placeholder="Explain why this request cannot be fulfilled"
                  value={denialReason}
                />
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeDenyDialog} type="button">
                Cancel
              </Button>
              <Button
                colorScheme="red"
                isLoading={denyMutation.isLoading}
                isDisabled={!denialReason.trim()}
                loadingText="Denying"
                ml="3"
                type="submit"
              >
                Deny request
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default Requests;
